import type { BeforeSendFn, CaptureResult } from 'posthog-js';

/**
 * Substrings that mark a non-actionable `$exception` — known, understood noise
 * we do not want cluttering PostHog's error tracking or eating into the
 * exception quota.
 *
 * Seeded from the error filters already living in the codebase:
 * - `components/error-boundry.tsx` video filter
 * - `components/agora-error-handler.tsx` (the embedded SDK's `initVideoElement`
 *   race)
 * - Next.js chunk-load failures (transient, caused by a deploy mid-session)
 */
export const EXCEPTION_NOISE_PATTERNS: string[] = [
  // error-boundry.tsx video filter
  'videoElement.play',
  'play is not a function',
  'video track',
  'this.videoElement',
  // agora-error-handler.tsx
  'this.videoElement.play is not a function',
  'initVideoElement',
  // chunk-load noise
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
];

interface ExceptionStackFrame {
  filename?: string;
  function?: string;
}

interface ExceptionListItem {
  type?: string;
  value?: string;
  stacktrace?: {
    frames?: ExceptionStackFrame[];
  };
}

function matchesNoise(text: string | undefined | null): boolean {
  if (!text) return false;
  const haystack = text.toLowerCase();
  return EXCEPTION_NOISE_PATTERNS.some((pattern) =>
    haystack.includes(pattern.toLowerCase()),
  );
}

/**
 * `before_send` hook: drop known-noise `$exception` events, pass everything else
 * through untouched.
 *
 * - `cr == null` → return `cr` (nothing to inspect).
 * - `cr.event !== '$exception'` → return `cr` untouched.
 * - For `$exception`: case-insensitively substring-match {@link EXCEPTION_NOISE_PATTERNS}
 *   against the first `$exception_list` entry's `value` / `type` and its stack
 *   frames' `filename` / `function`. Any match → return `null` (drop).
 * - Never throws: on any internal error it fails open and returns `cr` — better
 *   to keep a noisy event than to silently lose a real crash.
 */
export const dropExceptionNoise: BeforeSendFn = (
  cr: CaptureResult | null,
): CaptureResult | null => {
  try {
    if (cr == null) return cr;
    if (cr.event !== '$exception') return cr;

    const list = cr.properties?.$exception_list as
      | ExceptionListItem[]
      | undefined;
    const first = Array.isArray(list) ? list[0] : undefined;
    if (!first) return cr;

    if (matchesNoise(first.value) || matchesNoise(first.type)) {
      return null;
    }

    const frames = first.stacktrace?.frames;
    if (Array.isArray(frames)) {
      for (const frame of frames) {
        if (matchesNoise(frame?.filename) || matchesNoise(frame?.function)) {
          return null;
        }
      }
    }

    return cr;
  } catch {
    return cr;
  }
};
